import React, { useState } from 'react';
import comms from '../comms';
import RouteEditorSheet from './RouteEditorSheet';
import { fmtTime, rangesOverlap } from './schedulingUtils';

function Feedback({ msg, type }) {
  if (!msg) return null;
  return (
    <div style={{
      margin: '0 0 14px',
      padding: '12px 16px',
      borderRadius: 14,
      background: type === 'error' ? '#FEF2F2' : '#F0FDF4',
      borderLeft: `4px solid ${type === 'error' ? '#EF4444' : '#22C55E'}`,
      color: type === 'error' ? '#991B1B' : '#166534',
      fontSize: 13, fontWeight: 500, lineHeight: 1.5,
    }}>
      {msg}
    </div>
  );
}

function RouteCard({
  route,
  claimedRoutes,
  driverId,
  online,
  schedulingEnabled,
  isPast,
  onFeedback,
  onClaimed,
  onRouteUpdated,
  onReload,
  onEdit,
}) {
  const [claimingId, setClaimingId] = useState(null);

  const claimed = !!route.claimed;
  const closed = route.IsInactive === true || route.IsInactive === 1;
  const isClaiming = !!claimingId;

  // Location names: prefer explicit fields, fall back to start/end steps from DaySchedule
  const steps = Array.isArray(route.steps) ? route.steps : [];
  const startStep = steps.find((s) => s.type === 'start' || s.stepType === 'start');
  const endStep   = steps.find((s) => s.type === 'end'   || s.stepType === 'end');
  const startLocName = route.startLocationName || startStep?.locationName || startStep?.LocationName || '';
  const endLocName   = route.endLocationName   || endStep?.locationName   || endStep?.LocationName   || '';

  const currentVehicle = JSON.parse(localStorage.getItem('selectedVehicle') || 'null');
  const currentVehicleId = currentVehicle?.Id || currentVehicle?.id || null;
  const claimDisabled = isClaiming || !online || !currentVehicleId;

  const accentColor = closed ? '#CBD5E1' : claimed ? '#3B5BDB' : '#F59E0B';
  const statusBg    = closed ? '#F1F5F9' : claimed ? '#DBEAFE' : '#FEF3C7';
  const statusText  = closed ? '#94A3B8' : claimed ? '#1D4ED8' : '#92400E';
  const statusLabel = closed ? 'Closed' : claimed ? 'Claimed' : 'Available';

  const handleClaim = async () => {
    if (!online)           { onFeedback('Connect to claim a route.', 'error'); return; }
    if (!currentVehicleId) { onFeedback('Select your vehicle first.', 'error'); return; }
    const sameDay = claimedRoutes.filter(
      (c) => (c.ServiceDate || '').slice(0, 10) === (route.ServiceDate || '').slice(0, 10)
    );
    const clash = sameDay.find((c) =>
      rangesOverlap(route.StartDateTime, route.EndDateTime, c.StartDateTime, c.EndDateTime)
    );
    if (clash) {
      onFeedback(`Overlaps with ${clash.RouteName || 'another route'} — times must not overlap.`, 'error');
      return;
    }
    setClaimingId(route.RouteId);
    try {
      const res = await comms.claimRoute({ routeId: route.RouteId, driverId, vehicleId: currentVehicleId });
      if (res?.status === true) {
        const n = res.assignedTripCount;
        onFeedback(`Claimed! ${n ? `${n} trip${n === 1 ? '' : 's'} added.` : ''}`, 'success');
        onClaimed(route);
      } else if (res?.alreadyClaimed) {
        onFeedback('Just taken by another driver — reloading…', 'error');
        onReload();
      } else {
        onFeedback(res?.error || res?.message || "Couldn't claim — try again.", 'error');
      }
    } catch (e) {
      onFeedback(e?.message || "Couldn't claim — try again.", 'error');
    } finally {
      setClaimingId(null);
    }
  };

  return (
    <div style={{
      background: '#fff',
      borderRadius: 18,
      marginBottom: 14,
      boxShadow: '0 2px 14px rgba(15,23,42,0.08)',
      overflow: 'hidden',
      opacity: closed ? 0.72 : 1,
    }}>
      {/* Top accent bar */}
      <div style={{ height: 5, background: accentColor }} />

      <div style={{ padding: '14px 16px 16px' }}>
        {/* Route name + status badge */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 6 }}>
          <span style={{ fontSize: 16, fontWeight: 700, color: '#0F172A', flex: 1, lineHeight: 1.3 }}>
            {route.RouteName || 'Route'}
          </span>
          <span style={{
            fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 20,
            marginLeft: 8, flexShrink: 0,
            background: statusBg, color: statusText,
          }}>
            {statusLabel}
          </span>
        </div>

        {/* Journey display — start/end with connector line */}
        <div style={{
          background: '#F8FAFC', border: '1.5px solid #E2E8F0',
          borderRadius: 14, padding: '14px 16px', marginBottom: 12,
        }}>
          {/* Start row */}
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: 5 }}>
              <div style={{ width: 11, height: 11, borderRadius: '50%', background: '#22C55E', flexShrink: 0 }} />
              <div style={{ width: 2, height: 32, background: '#D1D5DB', margin: '4px 0' }} />
            </div>
            <div>
              <div style={{ fontSize: 20, fontWeight: 800, color: '#0F172A', lineHeight: 1.2 }}>
                {fmtTime(route.StartDateTime) || '—'}
              </div>
              <div style={{ fontSize: 12, color: startLocName ? '#64748B' : '#CBD5E1', marginTop: 2 }}>
                {startLocName || 'Start location'}
              </div>
            </div>
          </div>

          {/* End row */}
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
            <div style={{ paddingTop: 5 }}>
              <div style={{ width: 11, height: 11, borderRadius: 2, background: '#EF4444', flexShrink: 0 }} />
            </div>
            <div>
              <div style={{ fontSize: 20, fontWeight: 800, color: '#0F172A', lineHeight: 1.2 }}>
                {fmtTime(route.EndDateTime) || '—'}
              </div>
              <div style={{ fontSize: 12, color: endLocName ? '#64748B' : '#CBD5E1', marginTop: 2 }}>
                {endLocName || 'End location'}
              </div>
            </div>
          </div>

          {/* Break — shown inside card when set */}
          {(route.BreakStartDateTime || route.BreakEndDateTime) && (
            <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid #E2E8F0', fontSize: 12, color: '#94A3B8' }}>
              Break: {fmtTime(route.BreakStartDateTime)} – {fmtTime(route.BreakEndDateTime)}
            </div>
          )}
        </div>

        {/* Trip count */}
        {route.TripCount != null && (
          <div style={{ fontSize: 13, color: '#64748B', marginBottom: 8 }}>
            {route.TripCount} {route.TripCount === 1 ? 'trip' : 'trips'}
          </div>
        )}

        {/* Trip preview — from trips[] or itinerary pickup/delivery steps */}
        {(() => {
          // Trip steps live in different fields depending on where this route
          // came from: DriverSchedule/Bookings-shaped data uses "itinerary",
          // but claimed routes loaded via /API/Driver/DaySchedule attach the
          // real Route_Step rows as "steps" (see Scheduling.js's DaySchedule
          // fetch) — itinerary stays empty for those, so it must be checked too
          // or an assigned trip silently never shows here.
          const stepSource = Array.isArray(route.itinerary) && route.itinerary.length > 0
            ? route.itinerary
            : (Array.isArray(route.steps) ? route.steps : []);
          const flatTrips = Array.isArray(route.trips) && route.trips.length > 0
            ? route.trips.map((t) => ({
                key:  t.tripId || t.tripNo,
                name: t.passengerName || `Trip #${t.tripNo}`,
                time: fmtTime(t.pickupTime || t.time),
              }))
            : stepSource
                .filter((s) => s.type === 'pickup' || s.type === 'delivery' || s.type === 'dropoff')
                .map((s) => ({
                  key:  s.stepId || s.tripId || s.tripNo,
                  name: s.passengerName || (s.tripNo ? `Trip #${s.tripNo}` : s.locationName || ''),
                  time: fmtTime(s.time || s.arrivalTime),
                }));
          if (!flatTrips.length) return null;
          return (
            <div style={{
              marginTop: 10, marginBottom: 4,
              background: '#F8FAFC', borderRadius: 12, padding: '10px 12px',
            }}>
              {flatTrips.slice(0, 3).map((t) => (
                <div key={t.key} style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 5,
                }}>
                  <span style={{ fontSize: 12, color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                    {t.name}
                  </span>
                  <span style={{ fontSize: 12, color: '#94A3B8', flexShrink: 0, marginLeft: 8 }}>
                    {t.time}
                  </span>
                </div>
              ))}
              {flatTrips.length > 3 && (
                <div style={{ fontSize: 11, color: '#CBD5E1', textAlign: 'right', marginTop: 2 }}>
                  +{flatTrips.length - 3} more
                </div>
              )}
            </div>
          );
        })()}

        {/* Action buttons — hidden for past days, nothing to claim or edit anymore */}
        {!isPast && (
          <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
            {!claimed && (
              <button
                onClick={claimDisabled ? undefined : handleClaim}
                style={{
                  flex: 1, padding: '14px', borderRadius: 14, border: 'none',
                  background: claimDisabled ? '#F1F5F9' : '#16A34A',
                  color: claimDisabled ? '#94A3B8' : '#fff',
                  fontSize: 15, fontWeight: 800,
                  cursor: claimDisabled ? 'not-allowed' : 'pointer',
                  transition: 'opacity 0.15s, transform 0.1s',
                }}
                onMouseDown={(e) => { if (!claimDisabled) e.currentTarget.style.transform = 'scale(0.97)'; }}
                onMouseUp={(e) => { e.currentTarget.style.transform = ''; }}
                onTouchStart={(e) => { if (!claimDisabled) e.currentTarget.style.transform = 'scale(0.97)'; }}
                onTouchEnd={(e) => { e.currentTarget.style.transform = ''; }}
              >
                {isClaiming ? 'Claiming…' : 'Claim Route'}
              </button>
            )}

            {(claimed || !!route.IsOwnRoute) && (
              <button
                onClick={() => onEdit(route)}
                disabled={!online}
                style={{
                  flex: 1, padding: '14px', borderRadius: 14,
                  border: '2px solid #3B5BDB', background: '#EFF6FF',
                  color: '#3B5BDB', fontSize: 15, fontWeight: 700,
                  cursor: online ? 'pointer' : 'not-allowed',
                  opacity: online ? 1 : 0.5,
                }}
                onMouseDown={(e) => { if (online) e.currentTarget.style.transform = 'scale(0.97)'; }}
                onMouseUp={(e)   => { e.currentTarget.style.transform = ''; }}
                onTouchStart={(e)=> { if (online) e.currentTarget.style.transform = 'scale(0.97)'; }}
                onTouchEnd={(e)  => { e.currentTarget.style.transform = ''; }}
              >
                Edit Route
              </button>
            )}
          </div>
        )}

        {/* Past days are read-only — nothing left to claim or change */}
        {isPast && (
          <div style={{ textAlign: 'center', fontSize: 12, color: '#94A3B8', marginTop: 14 }}>
            This day has passed — no changes allowed
          </div>
        )}

        {/* Hint text when claim is disabled */}
        {!isPast && !claimed && (
          <div style={{ textAlign: 'center', fontSize: 12, color: '#94A3B8', marginTop: 8, minHeight: 16 }}>
            {!online
              ? 'Connect to the internet to claim'
              : !currentVehicleId
              ? 'Select your vehicle first'
              : ''}
          </div>
        )}
      </div>
    </div>
  );
}

function DayDetailSheet({
  routes,
  claimedRoutes,
  driverId,
  online,
  schedulingEnabled,
  isPast,
  onToast,
  onClaimed,
  onRouteUpdated,
  onReload,
  onSplit,
}) {
  const [editing, setEditing] = useState(null);
  const [feedback, setFeedback] = useState(null);

  const showFeedback = (msg, type = 'success') => {
    setFeedback({ msg, type });
    setTimeout(() => setFeedback(null), 4000);
  };

  return (
    <>
      <Feedback msg={feedback?.msg} type={feedback?.type} />

      {routes.map((route) => (
        <RouteCard
          key={route.RouteId || route.routeId}
          route={route}
          claimedRoutes={claimedRoutes}
          driverId={driverId}
          online={online}
          schedulingEnabled={schedulingEnabled}
          isPast={isPast}
          onFeedback={showFeedback}
          onClaimed={onClaimed}
          onRouteUpdated={onRouteUpdated}
          onReload={onReload}
          onEdit={setEditing}
        />
      ))}

      <RouteEditorSheet
        open={!!editing}
        onClose={() => setEditing(null)}
        route={editing}
        driverId={driverId}
        online={online}
        onToast={showFeedback}
        onSaved={(updated) => { onRouteUpdated(updated); setEditing(null); }}
        onSplit={onSplit}
      />
    </>
  );
}

export default DayDetailSheet;
